import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  FileText,
  Settings,
  LogOut,
  Mail,
  Eye,
  Trash2,
  Save,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  Lock,
  Home,
  Info,
  Wrench,
  Award,
  Truck,
  Users,
  Phone,
  BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Login Component
const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem("admin_token", response.data.access_token);
      onLogin();
      toast.success("Login successful");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_telecom-rigging/artifacts/9ue13u4p_Original%20on%20Transparent.png"
            alt="Quanticom"
            className="h-16 mx-auto mb-4"
          />
          <CardTitle className="text-2xl">Admin Dashboard</CardTitle>
          <p className="text-slate-500 text-sm mt-2">Sign in to manage your website content</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Username</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
                data-testid="admin-username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                data-testid="admin-password"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-teal-600 hover:bg-teal-700"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ activeTab, setActiveTab, onLogout, inquiryStats }) => {
  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "hero", label: "Hero Section", icon: Home },
    { id: "about", label: "About", icon: Info },
    { id: "stats", label: "Statistics", icon: BarChart3 },
    { id: "services", label: "Services", icon: Wrench },
    { id: "certifications", label: "Certifications", icon: Award },
    { id: "equipment", label: "Equipment", icon: Truck },
    { id: "partners", label: "Partners", icon: Users },
    { id: "contact", label: "Contact Info", icon: Phone },
    { id: "inquiries", label: "Inquiries", icon: Mail, badge: inquiryStats?.unread },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <aside className="w-64 bg-slate-900 min-h-screen p-4 flex flex-col">
      <div className="mb-8">
        <img 
          src="https://customer-assets.emergentagent.com/job_telecom-rigging/artifacts/9ue13u4p_Original%20on%20Transparent.png"
          alt="Quanticom"
          className="h-10 brightness-0 invert"
        />
      </div>
      
      <nav className="flex-1 space-y-1">
        {menuItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === item.id
                ? "bg-teal-600 text-white"
                : "text-slate-400 hover:bg-slate-800 hover:text-white"
            }`}
            data-testid={`nav-${item.id}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
            {item.badge > 0 && (
              <Badge className="ml-auto bg-red-500 text-white text-xs">{item.badge}</Badge>
            )}
          </button>
        ))}
      </nav>

      <div className="pt-4 border-t border-slate-700">
        <a
          href="/"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-white transition-colors mb-2"
        >
          <Eye className="w-5 h-5" />
          <span>View Website</span>
        </a>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-red-400 transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

// Dashboard Tab
const DashboardTab = ({ content, inquiryStats }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold">Dashboard Overview</h2>
    
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
              <Mail className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Inquiries</p>
              <p className="text-2xl font-bold">{inquiryStats?.total || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Unread Messages</p>
              <p className="text-2xl font-bold">{inquiryStats?.unread || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <Wrench className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Services Listed</p>
              <p className="text-2xl font-bold">{content?.services?.length || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    <Card>
      <CardHeader>
        <CardTitle>Quick Guide</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-slate-600">
        <p>• Use the sidebar to navigate between different sections of your website.</p>
        <p>• Edit text content directly and click "Save Changes" to update your website.</p>
        <p>• Check "Inquiries" regularly for new contact form submissions.</p>
        <p>• Update your password in "Settings" for security.</p>
      </CardContent>
    </Card>
  </div>
);

// Hero Editor
const HeroEditor = ({ content, onSave, saving }) => {
  const [hero, setHero] = useState(content?.hero || {});

  useEffect(() => {
    if (content?.hero) setHero(content.hero);
  }, [content]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Hero Section</h2>
        <Button onClick={() => onSave("hero", hero)} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Tagline (small text above title)</label>
            <Input
              value={hero.tagline || ""}
              onChange={(e) => setHero({...hero, tagline: e.target.value})}
              placeholder="e.g., Telecommunications Excellence"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Main Title</label>
            <Textarea
              value={hero.title || ""}
              onChange={(e) => setHero({...hero, title: e.target.value})}
              placeholder="Main headline..."
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <Textarea
              value={hero.description || ""}
              onChange={(e) => setHero({...hero, description: e.target.value})}
              placeholder="Brief company description..."
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Button 1 Text</label>
              <Input
                value={hero.button1_text || ""}
                onChange={(e) => setHero({...hero, button1_text: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Button 2 Text</label>
              <Input
                value={hero.button2_text || ""}
                onChange={(e) => setHero({...hero, button2_text: e.target.value})}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// About Editor
const AboutEditor = ({ content, onSave, saving }) => {
  const [about, setAbout] = useState(content?.about || {});

  useEffect(() => {
    if (content?.about) setAbout(content.about);
  }, [content]);

  const updateFeature = (index, value) => {
    const newFeatures = [...(about.features || [])];
    newFeatures[index] = value;
    setAbout({...about, features: newFeatures});
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">About Section</h2>
        <Button onClick={() => onSave("about", about)} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Subtitle</label>
            <Input
              value={about.subtitle || ""}
              onChange={(e) => setAbout({...about, subtitle: e.target.value})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <Input
              value={about.title || ""}
              onChange={(e) => setAbout({...about, title: e.target.value})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Paragraph 1</label>
            <Textarea
              value={about.paragraph1 || ""}
              onChange={(e) => setAbout({...about, paragraph1: e.target.value})}
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Paragraph 2</label>
            <Textarea
              value={about.paragraph2 || ""}
              onChange={(e) => setAbout({...about, paragraph2: e.target.value})}
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Features (4 highlight boxes)</label>
            <div className="grid grid-cols-2 gap-2">
              {(about.features || ["", "", "", ""]).map((feature, index) => (
                <Input
                  key={index}
                  value={feature}
                  onChange={(e) => updateFeature(index, e.target.value)}
                  placeholder={`Feature ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Stats Editor
const StatsEditor = ({ content, onSave, saving }) => {
  const [stats, setStats] = useState(content?.stats || []);

  useEffect(() => {
    if (content?.stats) setStats(content.stats);
  }, [content]);

  const updateStat = (index, field, value) => {
    const newStats = [...stats];
    newStats[index] = {...newStats[index], [field]: value};
    setStats(newStats);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Statistics</h2>
        <Button onClick={() => onSave("stats", stats)} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          {stats.map((stat, index) => (
            <div key={index} className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium mb-1">Number</label>
                <Input
                  value={stat.number || ""}
                  onChange={(e) => updateStat(index, "number", e.target.value)}
                  placeholder="e.g., 15+"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Label</label>
                <Input
                  value={stat.label || ""}
                  onChange={(e) => updateStat(index, "label", e.target.value)}
                  placeholder="e.g., Years Experience"
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// Services Editor
const ServicesEditor = ({ content, onSave, saving }) => {
  const [services, setServices] = useState(content?.services || []);

  useEffect(() => {
    if (content?.services) setServices(content.services);
  }, [content]);

  const updateService = (index, field, value) => {
    const newServices = [...services];
    newServices[index] = {...newServices[index], [field]: value};
    setServices(newServices);
  };

  const addService = () => {
    setServices([...services, { id: `service-${Date.now()}`, title: "", description: "", icon: "wrench" }]);
  };

  const removeService = (index) => {
    setServices(services.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Services</h2>
        <div className="flex gap-2">
          <Button onClick={addService} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Add Service
          </Button>
          <Button onClick={() => onSave("services", services)} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {services.map((service, index) => (
          <Card key={service.id || index}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="flex-1 space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Service Title</label>
                    <Input
                      value={service.title || ""}
                      onChange={(e) => updateService(index, "title", e.target.value)}
                      placeholder="Service name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <Textarea
                      value={service.description || ""}
                      onChange={(e) => updateService(index, "description", e.target.value)}
                      placeholder="Brief description of this service..."
                      rows={2}
                    />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeService(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// List Editor (for Certifications, Equipment)
const ListEditor = ({ title, content, field, onSave, saving, itemKey = "name" }) => {
  const [items, setItems] = useState(content?.[field] || []);

  useEffect(() => {
    if (content?.[field]) setItems(content[field]);
  }, [content, field]);

  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = { [itemKey]: value };
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { [itemKey]: "" }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">{title}</h2>
        <div className="flex gap-2">
          <Button onClick={addItem} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
          <Button onClick={() => onSave(field, items)} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-2">
          {items.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={item[itemKey] || ""}
                onChange={(e) => updateItem(index, e.target.value)}
                placeholder={`Enter ${title.toLowerCase().slice(0, -1)}...`}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeItem(index)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// Partners Editor
const PartnersEditor = ({ content, onSave, saving }) => {
  const [partners, setPartners] = useState(content?.partners || []);

  useEffect(() => {
    if (content?.partners) setPartners(content.partners);
  }, [content]);

  const updatePartner = (index, field, value) => {
    const newPartners = [...partners];
    newPartners[index] = {...newPartners[index], [field]: value};
    setPartners(newPartners);
  };

  const addPartner = () => {
    setPartners([...partners, { name: "", letter: "" }]);
  };

  const removePartner = (index) => {
    setPartners(partners.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Partners</h2>
        <div className="flex gap-2">
          <Button onClick={addPartner} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Add Partner
          </Button>
          <Button onClick={() => onSave("partners", partners)} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-3">
          {partners.map((partner, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={partner.name || ""}
                onChange={(e) => updatePartner(index, "name", e.target.value)}
                placeholder="Partner name"
                className="flex-1"
              />
              <Input
                value={partner.letter || ""}
                onChange={(e) => updatePartner(index, "letter", e.target.value.slice(0, 1).toUpperCase())}
                placeholder="Initial"
                className="w-20"
                maxLength={1}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removePartner(index)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// Contact Info Editor
const ContactEditor = ({ content, onSave, saving }) => {
  const [contact, setContact] = useState(content?.contact || {});

  useEffect(() => {
    if (content?.contact) setContact(content.contact);
  }, [content]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Contact Information</h2>
        <Button onClick={() => onSave("contact", contact)} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Phone Number</label>
            <Input
              value={contact.phone || ""}
              onChange={(e) => setContact({...contact, phone: e.target.value})}
              placeholder="0487 096 279"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email Address</label>
            <Input
              value={contact.email || ""}
              onChange={(e) => setContact({...contact, email: e.target.value})}
              placeholder="admin@quanticom.com.au"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Address Line 1</label>
            <Input
              value={contact.address_line1 || ""}
              onChange={(e) => setContact({...contact, address_line1: e.target.value})}
              placeholder="7 Mulholland Street"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Address Line 2</label>
            <Input
              value={contact.address_line2 || ""}
              onChange={(e) => setContact({...contact, address_line2: e.target.value})}
              placeholder="Kalkallo, VIC"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Inquiries Tab
const InquiriesTab = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem("admin_token");

  const fetchInquiries = async () => {
    const token = getToken();
    try {
      const response = await axios.get(`${API}/admin/inquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(response.data);
    } catch (error) {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const markAsRead = async (id) => {
    const token = getToken();
    try {
      await axios.put(`${API}/admin/inquiries/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(inquiries.map(inq => 
        inq.id === id ? {...inq, is_read: true} : inq
      ));
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const deleteInquiry = async (id) => {
    const token = getToken();
    if (!window.confirm("Are you sure you want to delete this inquiry?")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(inquiries.filter(inq => inq.id !== id));
      toast.success("Inquiry deleted");
    } catch (error) {
      toast.error("Failed to delete inquiry");
    }
  };

  if (loading) return <div className="text-center py-8">Loading inquiries...</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Contact Inquiries</h2>

      {inquiries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No inquiries yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {inquiries.map((inquiry) => (
            <Card key={inquiry.id} className={!inquiry.is_read ? "border-l-4 border-l-teal-500" : ""}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{inquiry.name}</h3>
                      {!inquiry.is_read && <Badge className="bg-teal-500">New</Badge>}
                      {inquiry.service_interest && (
                        <Badge variant="outline">{inquiry.service_interest}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mb-1">{inquiry.email} {inquiry.phone && `• ${inquiry.phone}`}</p>
                    <p className="text-slate-600 mt-3">{inquiry.message}</p>
                    <p className="text-xs text-slate-400 mt-3">
                      {new Date(inquiry.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!inquiry.is_read && (
                      <Button variant="outline" size="sm" onClick={() => markAsRead(inquiry.id)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => deleteInquiry(inquiry.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Settings Tab
const SettingsTab = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const getToken = () => localStorage.getItem("admin_token");

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    const token = getToken();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/change-password`, 
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Settings</h2>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium mb-1">Current Password</label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">New Password</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Confirm New Password</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" disabled={loading} className="bg-teal-600 hover:bg-teal-700">
              {loading ? "Updating..." : "Update Password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Admin Component
export default function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [content, setContent] = useState(null);
  const [inquiryStats, setInquiryStats] = useState({ total: 0, unread: 0 });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem("admin_token");

  const verifyAndLoad = async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsLoggedIn(true);
      await loadContent();
      await loadInquiryStats();
    } catch (error) {
      localStorage.removeItem("admin_token");
    } finally {
      setLoading(false);
    }
  };

  const loadContent = async () => {
    const token = getToken();
    try {
      const response = await axios.get(`${API}/admin/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(response.data);
    } catch (error) {
      toast.error("Failed to load content");
    }
  };

  const loadInquiryStats = async () => {
    const token = getToken();
    try {
      const response = await axios.get(`${API}/admin/inquiries/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiryStats(response.data);
    } catch (error) {
      console.error("Failed to load stats");
    }
  };

  useEffect(() => {
    verifyAndLoad();
  }, []);

  const handleLogin = async () => {
    setIsLoggedIn(true);
    await loadContent();
    await loadInquiryStats();
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setIsLoggedIn(false);
    setContent(null);
  };

  const handleSave = async (section, data) => {
    const token = getToken();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/${section}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} saved successfully`);
      await loadContent();
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return <LoginForm onLogin={handleLogin} />;
  }

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <DashboardTab content={content} inquiryStats={inquiryStats} />;
      case "hero":
        return <HeroEditor content={content} onSave={handleSave} saving={saving} />;
      case "about":
        return <AboutEditor content={content} onSave={handleSave} saving={saving} />;
      case "stats":
        return <StatsEditor content={content} onSave={handleSave} saving={saving} />;
      case "services":
        return <ServicesEditor content={content} onSave={handleSave} saving={saving} />;
      case "certifications":
        return <ListEditor title="Certifications" content={content} field="certifications" onSave={handleSave} saving={saving} />;
      case "equipment":
        return <ListEditor title="Equipment" content={content} field="equipment" onSave={handleSave} saving={saving} />;
      case "partners":
        return <PartnersEditor content={content} onSave={handleSave} saving={saving} />;
      case "contact":
        return <ContactEditor content={content} onSave={handleSave} saving={saving} />;
      case "inquiries":
        return <InquiriesTab />;
      case "settings":
        return <SettingsTab />;
      default:
        return <DashboardTab content={content} inquiryStats={inquiryStats} />;
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-100">
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        onLogout={handleLogout}
        inquiryStats={inquiryStats}
      />
      <main className="flex-1 p-8">
        {renderContent()}
      </main>
    </div>
  );
}
